import { z } from "zod";

// ---------------------------------------------------------------------------
// Field control types — shared between backend resolvers and frontend renderers
// ---------------------------------------------------------------------------

/** UI control type hint for interactive field rendering */
export type FieldControl =
  | "text"
  | "select"
  | "date"
  | "hidden"
  | "readonly"
  | "textarea";

/**
 * Structured option for a `select` field.
 *
 * `id` is the stable value persisted/submitted to downstream systems.
 * `name` is the human-readable label shown in the dropdown. Resolvers may
 * emit plain strings (value == label) or these structured objects when the
 * display label differs from the stored code.
 */
export const SelectOptionSchema = z.object({
  id: z.union([z.string(), z.number()]),
  name: z.string(),
});
export type SelectOption = z.infer<typeof SelectOptionSchema>;

/**
 * A single entry in `FieldEntry.selectOptions`. Either a plain string
 * (value == label) or a structured `SelectOption`.
 */
export const FieldSelectOptionSchema = z.union([
  z.string(),
  SelectOptionSchema,
]);
export type FieldSelectOption = z.infer<typeof FieldSelectOptionSchema>;

// ---------------------------------------------------------------------------
// Field entry — unified field configuration for all UI surfaces
// ---------------------------------------------------------------------------

/**
 * Configuration entry for a single field within a UI section.
 *
 * Used by both table surfaces (sortable, filterable columns) and card/detail
 * surfaces (editable label-value pairs). Surface-specific properties are
 * optional — rendering components read only the properties relevant to them
 * and ignore the rest.
 */
export interface FieldEntry {
  /** Unique identifier for this field instance */
  id: string;
  /** Display label (shown as column header in tables, field label in cards) */
  label: string;
  /**
   * Dot-notation path to resolve the value from the data object (e.g.
   * "originMetadata.site_use_code"). Optional: resolver-driven fields (e.g.
   * `extracted_data_field`) compute their value and carry no dataPath.
   */
  dataPath?: string;
  /** Display format hint — determines how raw values are rendered */
  format?:
    | "string"
    | "number"
    | "integer"
    | "float"
    | "currency"
    | "date"
    | "boolean";

  // --- Table-specific ---
  /** Whether the column supports backend sorting */
  sortable?: boolean;
  /** Whether the column appears as a filter dimension */
  filterable?: boolean;
  /** Whether the column is right-aligned (for numbers/currency) */
  alignRight?: boolean;
  /** Whether the field is hidden from the UI but still drives filters */
  hidden?: boolean;
  /** Whether the field is enabled/visible in the UI (takes precedence over hidden) */
  enabled?: boolean;
  /** If true, the field is omitted from rendering when its resolved value is null/empty */
  hideWhenEmpty?: boolean;

  // --- Card-specific ---
  /** Whether the field renders an interactive edit widget instead of plain text */
  editable?: boolean;
  /** Optional help text shown as a tooltip next to the field label. */
  tooltip?: string;
  /**
   * Horizontal span on a card's column grid. On the sales-order review page's
   * 12-column header grid: 1=¼, 2=½, 3=¾, 4=full, 5=⅓ width. Defaults to 1.
   */
  colSpan?: 1 | 2 | 3 | 4 | 5;
  /** Widget type hint for future rendering variations (e.g. "badge", "pill-list", "link") */
  widget?: string;

  /** Catch-all for future surface-specific properties */
  options?: Record<string, unknown>;

  /**
   * Admin-defined dropdown options for `control: "select"` fields (persisted).
   * When set, these replace the code-derived option source (Oracle EBS/Fusion
   * maps, option sets, synced enums) on the sales-order review page. `value`
   * is the stored/submitted value. Distinct from the runtime-injected
   * `selectOptions` below.
   */
  dropdownOptions?: { value: string; label: string }[];

  // --- Resolver ---
  /** Registered field resolver to compute this field's value at runtime (e.g., "planner_code_order_type") */
  resolver?: string;

  // --- Field grouping ---
  /** Which field group this entry belongs to (determines where it renders) */
  fieldGroup?: "header" | "lineItem";

  // --- Control & resolved values (injected by backend resolvers at runtime, never persisted in DB config) ---
  /** UI control type hint — determines which interactive widget to render */
  control?: FieldControl;
  /** Backend-resolved value — injected at runtime by resolvers, never stored in DB */
  resolvedValue?: unknown;
  /** Available options for select controls — injected at runtime by resolvers (strings or objects with id/name) */
  selectOptions?: FieldSelectOption[];
}

/** The surface type that a UI section renders as */
export type UiSectionType = "table" | "card" | "list" | "form";

// ---------------------------------------------------------------------------
// Layout tree — controls spatial arrangement of fields and widgets
// ---------------------------------------------------------------------------

/** A leaf node that renders a field from the fields map */
export interface FieldLayoutNode {
  type: "field";
  /** References a FieldEntry by its `id` */
  fieldId: string;
}

/** A leaf node that renders a standalone widget (not tied to a field) */
export interface WidgetLayoutNode {
  type: "widget";
  /** References a key in the widgetRegistry */
  widget: string;
}

/** Arranges children horizontally */
export interface RowLayoutNode {
  type: "row";
  children: LayoutNode[];
}

/** Arranges children vertically */
export interface ColumnLayoutNode {
  type: "column";
  children: LayoutNode[];
}

/** A labeled group of fields, optionally collapsible */
export interface GroupLayoutNode {
  type: "group";
  label: string;
  collapsible?: boolean;
  children: LayoutNode[];
}

/**
 * Recursive layout tree node.
 *
 * Developers define a default layout in code. Orgs can override it via the
 * `layout` column in `core_org_ui_section_configs`. When no layout is present,
 * the renderer falls back to a flat field list.
 */
export type LayoutNode =
  | FieldLayoutNode
  | WidgetLayoutNode
  | RowLayoutNode
  | ColumnLayoutNode
  | GroupLayoutNode;
